import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { ChevronLeft, ChevronUp, Square } from 'lucide-react';
import { getQuestionText, HOMEWORK_STATE } from '@/lib/utils';
import { askChatbot } from '@/services/generatedAPIs';
import { QuestionModel } from '@/services/generatedAPIs.schemas';
import { useAuth } from '@/contexts/AuthContext';
import { useAsyncAction } from '@/hooks/use-async-action';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import MobileLandscapeDashboardShell, { useStageFit } from './mobile-landscape-dashboard-shell';

// 手机横屏(844×390)AI 提问页主体:标题 + 聊天面板(消息区/建议 chip/输入行);业务逻辑复用桌面 AITutorPage,仅横屏呈现

const ACCENT = 'var(--practice-dialog-accent)';
const ACCENT_FG = 'var(--practice-dialog-accent-fg)';
const CARD_BG = '#F3F3F4';

interface Msg {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

// 仅手机横屏(高≤500)挂载,桌面/平板返回 null(聊天由 AITutorPage 渲染),避免重复挂一份聊天状态
function useMobileLandscape() {
  const [is, setIs] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-height: 500px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-height: 500px)');
    const on = () => setIs(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return is;
}

export default function MobileLandscapeAITutor() {
  const isMobileLandscape = useMobileLandscape();
  if (!isMobileLandscape) return null;
  return <Inner />;
}

function Inner() {
  const { getCurrentUser } = useAuth();
  const navigate = useNavigate();
  const state = useLocation().state as { questionModel?: QuestionModel; assignmentId?: number; tabId?: string; itemId?: number };
  const { t, i18n } = useTranslation();
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  // 宽屏铺满:取与舞台壳一致的 shift(见 useStageFit)。聊天面板吃掉 shift 变宽(输入行 send 键 absolute right 自然贴右)。
  const { shift } = useStageFit();

  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [firstContent, setFirstContent] = useState('');
  const [sessionId] = useState(() => Date.now().toString());
  const scrollRef = useRef<HTMLDivElement>(null);

  const hasQuestion = !!(state && state.questionModel);

  // 开场消息(assistant 播报题目 + 学生回答),与桌面 AITutorPage 一致
  useEffect(() => {
    if (hasQuestion) {
      const newContent = t('student.botopening')
        .replace('%s', getQuestionText(state.questionModel!.questionInfo, i18n))
        .replace('%s', state.questionModel!.question?.answer ? state.questionModel!.question!.answer : '');
      setFirstContent(newContent);
      setMessages([{ id: '1', role: 'assistant', content: newContent }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 新消息 / 加载态变化时滚到底部
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isLoading]);

  const send = (text: string) => {
    const value = text.trim();
    if (!value || isLoading) return;
    const isFirstSubmit = messages.length === 1;
    const next: Msg[] = [...messages, { id: '1', role: 'user', content: value }];
    setMessages(next);
    setInput('');
    execute(async () => {
      try {
        const rsp = await askChatbot({
          chatId: getCurrentUser()!.id.toString() + ':' + state?.questionModel?.question?.id + ':' + sessionId,
          message: (isFirstSubmit ? firstContent : '') + value,
          questionId: state?.questionModel?.question?.id,
          assignmentId: state?.assignmentId,
        });
        setMessages((prev) => [...prev, { id: '1', role: 'assistant', content: rsp.data!.message }]);
      } catch (error) {
        setMessages((prev) => [...prev, { id: '1', role: 'assistant', content: 'Error: ' + (error as Error).message }]);
      }
    });
  };

  const onSubmit = (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    send(input);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const goBack = () => {
    if (state?.tabId) HOMEWORK_STATE.tabId = state.tabId;
    if (state?.itemId !== undefined) HOMEWORK_STATE.itemId = state.itemId;
    navigate(-1);
  };

  // 消息气泡
  const bubble = (m: Msg, idx: number) => {
    const isUser = m.role === 'user';
    return (
      <div key={idx} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
        <div
          style={{
            maxWidth: '86%', borderRadius: 10, padding: '8px 14px', fontSize: 12, lineHeight: 1.5,
            fontFamily: 'var(--font-sans)', wordBreak: 'break-word',
            background: isUser ? ACCENT : '#FFFFFF',
            color: isUser ? ACCENT_FG : '#1A1C1D',
            boxShadow: isUser ? 'none' : '0 0 11px 0 rgba(0,0,0,0.10)',
          }}
          className="[&_.space-y-3>*+*]:mt-2 [&_p]:whitespace-pre-wrap"
        >
          <MarkdownRenderer>{m.content}</MarkdownRenderer>
        </div>
      </div>
    );
  };

  // 提问建议 chip(点选填入输入框,不自动发送——与桌面 PromptButtonsMessage 一致)
  const chip = (label: string) => (
    <button
      key={label}
      type="button"
      onClick={() => setInput(label)}
      style={{
        borderRadius: 11, background: '#FFFFFF', border: 0, cursor: 'pointer',
        padding: '5px 12px', fontSize: 12, color: '#8F8F8F', fontFamily: 'var(--font-sans)',
        whiteSpace: 'nowrap', boxShadow: '0 0 8px 0 rgba(0,0,0,0.06)',
      }}
      className="transition-colors hover:text-foreground"
    >
      {label}
    </button>
  );

  const content = (
    <>
      {/* 页面标题(与 dashboard 各页同 h1 坐标/尺寸) */}
      <h1 style={{ position: 'absolute', left: 204, top: 71, margin: 0, fontSize: 20, fontWeight: 600, color: '#000', fontFamily: 'var(--font-sans)', lineHeight: '20px' }}>
        {t('component.exttitle')}
      </h1>

      {/* 聊天面板 */}
      <div style={{ position: 'absolute', left: 206, top: 101, width: 627 + shift, height: 277, borderRadius: 20, background: CARD_BG, display: 'flex', flexDirection: 'column', padding: '16px 17px 12px' }}>
        {/* 返回題目(仅从题目「問 AI」进入时;设计稿未画,保留以免丢失返回导航) */}
        {hasQuestion && (
          <button
            type="button"
            onClick={goBack}
            style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 8, background: 'transparent', border: 0, cursor: 'pointer', color: 'rgba(26,28,29,0.6)', fontFamily: 'var(--font-sans)', flexShrink: 0 }}
            className="transition-colors hover:text-foreground"
          >
            <ChevronLeft size={14} />
            <span style={{ fontSize: 11 }}>{t('component.backtoquiz')}</span>
          </button>
        )}

        {/* 消息滚动区 */}
        <div ref={scrollRef} className="ml-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
          {messages.map(bubble)}
          {/* 生成中:淡入的「思考」气泡 */}
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ borderRadius: 10, padding: '10px 14px', background: '#FFFFFF', boxShadow: '0 0 11px 0 rgba(0,0,0,0.10)', display: 'flex', gap: 4 }}>
                {[0, 1, 2].map((i) => (
                  <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: '#8F8F8F', animation: 'ml-ai-bounce 1s infinite', animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 提问建议 chip 行(从题目进入时展示三个默认建议) */}
        {hasQuestion && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, marginBottom: 8, flexShrink: 0 }}>
            {[t('student.botoption1'), t('student.botoption2'), t('student.botoption3')].map(chip)}
          </div>
        )}

        {/* 输入行 */}
        <form onSubmit={onSubmit} style={{ flexShrink: 0 }}>
          <div style={{ position: 'relative', height: 45, background: '#FFFFFF', borderRadius: 10, display: 'flex', alignItems: 'center' }}>
            <textarea
              value={input}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('student.aiPlaceholder')}
              rows={1}
              className="ml-thin-scroll placeholder:text-[#8F8F8F]"
              style={{ flex: 1, height: '100%', resize: 'none', border: 0, outline: 'none', background: 'transparent', padding: '14px 52px 14px 18px', fontSize: 12, lineHeight: '17px', color: '#1A1C1D', fontFamily: 'var(--font-sans)' }}
            />
            <button
              type="submit"
              disabled={input.trim() === '' || isLoading}
              aria-label="Send message"
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 30, height: 30, borderRadius: 8, background: ACCENT, color: ACCENT_FG, border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              className="transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              {isLoading
                ? <Square style={{ width: 12, height: 12 }} className="text-white/80" fill="currentColor" stroke="none" />
                : <ChevronUp style={{ width: 18, height: 18 }} strokeWidth={2.5} />}
            </button>
          </div>
        </form>
      </div>

      {/* 三点跳动关键帧 */}
      <style>{`@keyframes ml-ai-bounce{0%,80%,100%{transform:translateY(0);opacity:.5}40%{transform:translateY(-3px);opacity:1}}`}</style>
    </>
  );

  return createPortal(
    <div
      className="hidden [@media(max-height:500px)]:flex fixed inset-0 items-center justify-center overflow-hidden"
      style={{ background: 'var(--practice-page-bg)' }}
    >
      <MobileLandscapeDashboardShell>{content}</MobileLandscapeDashboardShell>
    </div>,
    document.body,
  );
}
